import React, { useState } from 'react';
import { Modal,
  Form,
  Input,
  Select,
  Button, 
  Card,
  Row,
  Col,
  Tag,
 } from 'antd';
import { useNavigate } from 'react-router-dom';
// 使用 window.message 代替 antd 静态方法，避免 context 警告
import {
  TOPIC_OPTIONS,
  MASK_IMAGE_ENUM,
  CARD_BG_ENUM,
  PROPOSAL_TOPIC_OPTIONS,
  PROPOSAL_TOPIC_OPTIONS_NO_TEMPLATE
} from '../../utils/constants';
import competitionService from '../../services/competitionService';
import { getCurrentUser } from '../../services/authService';

const { Option } = Select;

const TEMPLATE_LIST = [
  {
    key: 'ai_competition_model',
    title: '燕缘·滴水湖大学生AI+国际创业大赛模板',
    description: '适用于AI创新、竞赛、项目孵化等场景，包含标准提案结构',
  },
];

const CreateProposalModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: 选择模板, 2: 填写内容
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const navigate = useNavigate();


  // 切换到第二步
  const goToStep2 = (templateKey = null) => {
    setSelectedTemplate(templateKey);
    setStep(2);
  };

  // 处理场景选择变化
  const handleTopicsChange = (values) => {
    form.setFieldsValue({ topics: values });
  };

  // 随机生成 mask
  const getRandomMask = () => {
    const maskImages = Object.keys(MASK_IMAGE_ENUM);
    const cardColors = Object.keys(CARD_BG_ENUM);
    // 生成所有组合
    const allCombinations = [];
    for (const image of maskImages) {
      for (const color of cardColors) {
        allCombinations.push({ image, color });
      }
    }
    // 随机选一个组合
    const randomIndex = Math.floor(Math.random() * allCombinations.length);
    return allCombinations[randomIndex];
  };

  // 提交表单
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const currentUser = getCurrentUser();
      const mask = getRandomMask();
      let newTopics =['ai_competition'];
      
      // 如果选择了模板，场景是必填的
      if (selectedTemplate) {
        if (!values.topics || values.topics.length === 0) {
          throw new Error('请选择场景');
        }
        newTopics = [...newTopics, values.topics, 'ai_competition_model'];
      } else {
        // 如果没有选择模板，场景是可选的
        if (values.topics && values.topics.length > 0) {
          newTopics = [...newTopics, values.topics];
        }
      }
      
      await competitionService.createEntry({
        ...values,
        created_by_id: currentUser?.id,
        scope: 'square',
        template: selectedTemplate || undefined,
        status: 'draft',
        mask, // 新增mask字段
        topics: newTopics,
        category:'competition'
      });
      window.message?.success('提案创建成功');
      form.resetFields();
      onSuccess?.();
      onCancel();
      localStorage.setItem('activate_tasks_menu', 'true');
      navigate('/tasks', {
        state: {
          refreshList: true,
          activateTasksMenu: true
        }
      });
    } catch (err) {
      if (err?.errorFields) return;
      window.message?.error(err.message || '创建失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 关闭时重置步骤
  const handleCancel = () => {
    setStep(1);
    setSelectedTemplate(null);
    form.resetFields();
    onCancel();
  };

  // 模板选择页
  const renderTemplateStep = () => (
    <div style={{ minHeight: 220 }}>
      <div style={{ marginBottom: 24, fontWeight: 500, fontSize: 16 }}>
        请选择提案模板
      </div>
      <Row gutter={16}>
        {TEMPLATE_LIST.map((tpl) => (
          <Col key={tpl.key} span={24}>
            <Card
              hoverable
              onClick={() => setSelectedTemplate(tpl.key)}
              style={{
                borderColor:
                  selectedTemplate === tpl.key ? '#1677ff' : '#f0f0f0',
                boxShadow:
                  selectedTemplate === tpl.key
                    ? '0 0 0 2px #1677ff33'
                    : undefined,
                marginBottom: 12,
                cursor: 'pointer',
                borderRadius: 12,
              }}
              bodyStyle={{ padding: 20 }}
            >
              <div style={{ fontWeight: 600, fontSize: 15 }}>{tpl.title}</div>
              <div style={{ color: '#888', marginTop: 6 }}>
                {tpl.description}
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      {/* 底部按钮 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
          marginTop: 32,
        }}
      >
        <Button onClick={() => goToStep2(null)} style={{ minWidth: 120 }}>
          不用模板，直接创建
        </Button>
        <Button
          type="primary"
          disabled={!selectedTemplate}
          onClick={() => goToStep2(selectedTemplate)}
          style={{ minWidth: 120 }}
        >
          确认
        </Button>
      </div>
    </div>
  );

  // 创建提案表单页
  const [descPreview, setDescPreview] = useState('');
  const renderFormStep = () => (
    <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
      <Form.Item
        label={<span style={{ fontWeight: 500 }}>标题</span>}
        name="prompt"
        rules={[{ required: true, message: '请输入标题' }]}
      >
        <Input
          maxLength={30}
          placeholder="[项目名称]——支持[目标用户][核心需求]的[产品类型]"
          style={{ borderRadius: 8, height: 40 }}
        />
      </Form.Item>
      <Form.Item
        label={<span style={{ fontWeight: 500 }}>简介</span>}
        name="response"
        rules={[{ required: true, message: '请输入简介' }]}
      >
        <Input.TextArea
          rows={4}
          maxLength={200}
          placeholder={`本提案目标用户是[用户画像]，他们遇到了[核心痛点]。\n我们的产品是[名称/方向]，相比现有方案，AI可以[如何用AI解决这个痛点]。\n我们是实现这个想法的最佳人选，因为[创意来源/团队优势/前期基础]。`}
          
          style={{ borderRadius: 8, marginBottom: 8 }}
        />
      </Form.Item>
      <Form.Item
        label={<span style={{ fontWeight: 500 }}>场景</span>}
        name="topics"
        rules={[
          {
            required: selectedTemplate !== null, // 如果选择了模板（包含ai_competition_model），则场景必填
            message: '请选择场景'
          }
        ]}
      >
        <Select
          placeholder="请选择场景"
          style={{ borderRadius: 8 }}
          onChange={handleTopicsChange}
          tagRender={(props) => {
            // 让“ai_competition”不可删除
            if (props.value === 'ai_competition') {
              return (
                <span
                  style={{
                    background: '#e6f4ff',
                    color: '#1677ff',
                    borderRadius: 4,
                    padding: '0 8px',
                    marginRight: 3,
                    display: 'inline-block',
                  }}
                >
                  {props.label}
                </span>
              );
            }
            // 让“ai_competition_model”不可删除（如果选了模板）
            if (props.value === 'ai_competition_model' && selectedTemplate) {
              return (
                <span
                  style={{
                    background: '#e6f4ff',
                    color: '#1677ff',
                    borderRadius: 4,
                    padding: '0 8px',
                    marginRight: 3,
                    display: 'inline-block',
                  }}
                >
                  {props.label}
                </span>
              );
            }
            // 其它正常
            return (
              <Tag
                color="blue"
                closable={props.closable}
                onClose={props.onClose}
                style={{ marginRight: 3 }}
              >
                {props.label}
              </Tag>
            );
          }}
        >
          {PROPOSAL_TOPIC_OPTIONS_NO_TEMPLATE.map((opt) => (
            <Option
              key={opt.value}
              value={opt.value}
            >
              {opt.label}
            </Option>
          ))}
        </Select>
      </Form.Item>
    </Form>
  );

  return (
    <Modal
      title={step === 1 ? '创建提案' : '填写提案内容'}
      open={visible}
      onCancel={handleCancel}
      onOk={step === 2 ? handleOk : undefined}
      okText={step === 2 ? '确认创建' : undefined}
      confirmLoading={loading}
      width={520}
      destroyOnHidden
      footer={null}
    >
      {step === 1 ? (
        renderTemplateStep()
      ) : (
        <>
          {renderFormStep()}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
              marginTop: 24,
            }}
          >
            <Button onClick={handleCancel} style={{ minWidth: 120 }}>
              取消
            </Button>
            <Button
              type="primary"
              loading={loading}
              onClick={handleOk}
              style={{ minWidth: 120 }}
            >
              确认创建
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default CreateProposalModal;
